import { Controller } from '@hotwired/stimulus';
import { Lithuanian } from 'flatpickr/dist/l10n/lt';
import { english } from 'flatpickr/dist/l10n/default';

const LT_MONTHS = [
    'Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis',
    'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis',
];

const AGE_THUMB_WIDTH = 18;

export default class extends Controller {
    static targets = [
        'form', 'panel', 'toggle', 'content',
        'ageRange', 'ageValue', 'dates',
        'durationList', 'durationSection',
        'monthList', 'monthSection',
        'sectionList', 'sectionSection',
        'empty',
    ];

    connect() {
        super.connect();
        this.selectedAge = 0;
        this.dateRange = null;
        this.cards = Array.from(document.querySelectorAll('[data-camp-card]'));
        this.sections = Array.from(document.querySelectorAll('[data-camp-list-section]'));

        this.buildOptions();
        this.initAgeSlider();
        this.initDates();

        if (this.hasFormTarget) {
            this.formTarget.addEventListener('change', () => this.filter());
        }

        this.initMobileToggle();
        this.filter();
    }

    buildOptions() {
        if (this.hasDurationListTarget) {
            const durations = new Map();
            this.cards.forEach((card) => {
                this.parseJsonAttr(card, 'durations').forEach((duration) => {
                    if (!durations.has(duration.value)) {
                        durations.set(duration.value, duration);
                    }
                });
            });

            const options = Array.from(durations.values()).sort((a, b) => (a.sort || 0) - (b.sort || 0));

            if (options.length === 0 && this.hasDurationSectionTarget) {
                this.durationSectionTarget.classList.add('d-none');
            }

            this.durationListTarget.innerHTML = options
                .map((duration) => this.checkboxHtml('duration', duration.value, duration.label))
                .join('');
        }

        if (this.hasMonthListTarget) {
            const months = new Set();
            this.cards.forEach((card) => {
                (card.dataset.months || '').split(',').filter(Boolean).forEach((month) => months.add(parseInt(month, 10)));
            });

            const sorted = Array.from(months).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);

            if (sorted.length === 0 && this.hasMonthSectionTarget) {
                this.monthSectionTarget.classList.add('d-none');
            }

            this.monthListTarget.innerHTML = sorted
                .map((month) => this.checkboxHtml('month', month, LT_MONTHS[month - 1]))
                .join('');
        }

        if (this.hasSectionListTarget) {
            const sections = new Set();
            this.cards.forEach((card) => {
                const title = card.dataset.sectionTitle;

                if (title) {
                    sections.add(title);
                }
            });

            const options = Array.from(sections).sort((a, b) => a.localeCompare(b));

            if (options.length === 0 && this.hasSectionSectionTarget) {
                this.sectionSectionTarget.classList.add('d-none');
            }

            this.sectionListTarget.innerHTML = options
                .map((title) => this.checkboxHtml('section', title, title))
                .join('');
        }
    }

    escapeAttr(value) {
        return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    }

    checkboxHtml(name, value, label) {
        const id = `camp-filter-${name}-${value}`.replace(/[^\w-]/g, '');

        return `<div class="camp-filter__checkbox">
            <input type="checkbox" name="${name}[]" value="${this.escapeAttr(value)}" id="${id}">
            <label for="${id}">${label}</label>
        </div>`;
    }

    initAgeSlider() {
        if (!this.hasAgeRangeTarget || !this.hasAgeValueTarget) {
            return;
        }

        const range = this.ageRangeTarget;
        const valueEl = this.ageValueTarget;

        this.updateAgeSlider = () => {
            const min = parseInt(range.min || '0', 10);
            const max = parseInt(range.max || '18', 10);
            const val = parseInt(range.value || '0', 10);

            const trackWidth = range.getBoundingClientRect().width;
            const ratio = (val - min) / (max - min || 1);
            const posPx = ratio * (trackWidth - AGE_THUMB_WIDTH) + (AGE_THUMB_WIDTH / 2);

            range.style.backgroundSize = `${posPx}px 100%`;
            valueEl.style.left = `${posPx}px`;
            valueEl.textContent = val === 0 ? 'Visi' : String(val);
        };

        this.updateAgeSlider();

        range.addEventListener('input', () => {
            this.selectedAge = parseInt(range.value || '0', 10);
            this.updateAgeSlider();
            this.filter();
        });

        window.addEventListener('resize', this.updateAgeSlider);
    }

    async initDates() {
        if (!this.hasDatesTarget) {
            return;
        }

        const flatpickr = (await import('flatpickr')).default;

        this.picker = flatpickr(this.datesTarget, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            disableMobile: true,
            locale: document.documentElement.lang.split('-')[0] === 'lt' ? Lithuanian : english,
            onChange: (dates) => {
                this.dateRange = dates.length === 2 ? dates : null;
                this.filter();
            },
        });
    }

    parseJsonAttr(element, name) {
        try {
            const parsed = JSON.parse(element.dataset[name] || '[]');

            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    checkedValues(name) {
        if (!this.hasFormTarget) {
            return [];
        }

        return Array.from(this.formTarget.querySelectorAll(`input[name="${name}[]"]:checked`)).map((input) => input.value);
    }

    filter() {
        const durations = this.checkedValues('duration');
        const months = this.checkedValues('month').map(Number);
        const sections = this.checkedValues('section');
        let visibleCount = 0;

        this.cards.forEach((card) => {
            const visible = this.matchAge(card)
                && this.matchDurations(card, durations)
                && this.matchMonths(card, months)
                && this.matchSections(card, sections)
                && this.matchDates(card);

            card.classList.toggle('d-none', !visible);

            if (visible) {
                visibleCount++;
            }
        });

        // Hide whole sections (header included) when every card inside is filtered out
        this.sections.forEach((section) => {
            const anyVisible = section.querySelector('[data-camp-card]:not(.d-none)') !== null;
            section.classList.toggle('d-none', !anyVisible);
        });

        if (this.hasEmptyTarget) {
            this.emptyTarget.classList.toggle('d-none', visibleCount > 0);
        }
    }

    matchAge(card) {
        if (!this.selectedAge) {
            return true;
        }

        const ranges = (card.dataset.age || '').split(',').filter(Boolean);

        if (ranges.length === 0) {
            return false;
        }

        return ranges.some((segment) => {
            segment = segment.trim();

            if (segment.includes('-')) {
                const [minAge, maxAge] = segment.split('-').map((a) => parseInt(a.trim(), 10));

                return this.selectedAge >= minAge && this.selectedAge <= maxAge;
            }

            return this.selectedAge === parseInt(segment, 10);
        });
    }

    matchDurations(card, selected) {
        if (selected.length === 0) {
            return true;
        }

        const cardDurations = this.parseJsonAttr(card, 'durations').map((d) => d.value);

        return selected.some((value) => cardDurations.includes(value));
    }

    matchMonths(card, selected) {
        if (selected.length === 0) {
            return true;
        }

        const cardMonths = (card.dataset.months || '').split(',').filter(Boolean).map(Number);

        return selected.some((month) => cardMonths.includes(month));
    }

    matchSections(card, selected) {
        if (selected.length === 0) {
            return true;
        }

        return selected.includes(card.dataset.sectionTitle || '');
    }

    matchDates(card) {
        if (!this.dateRange) {
            return true;
        }

        const sessions = this.parseJsonAttr(card, 'sessions');

        if (sessions.length === 0) {
            return false;
        }

        const [from, to] = this.dateRange;
        const fromTime = from.getTime();
        const toTime = to.getTime();

        return sessions.some((session) => {
            const start = new Date(`${session.start}T00:00:00`).getTime();
            const end = new Date(`${session.end}T23:59:59`).getTime();

            return start <= toTime && end >= fromTime;
        });
    }

    clear(event) {
        if (event) {
            event.preventDefault();
        }

        if (this.hasFormTarget) {
            this.formTarget.reset();
        }

        this.selectedAge = 0;

        if (this.hasAgeRangeTarget) {
            this.ageRangeTarget.value = '0';
        }

        if (this.updateAgeSlider) {
            this.updateAgeSlider();
        }

        this.dateRange = null;

        if (this.picker) {
            this.picker.clear();
        }

        this.filter();
    }

    toggle(event) {
        if (event) {
            event.preventDefault();
        }

        if (!this.mq || !this.mq.matches) {
            return;
        }

        this.panelTarget.classList.toggle('collapsed');

        if (this.hasToggleTarget) {
            this.toggleTarget.setAttribute('aria-expanded', String(!this.panelTarget.classList.contains('collapsed')));
        }
    }

    initMobileToggle() {
        if (!this.hasPanelTarget) {
            return;
        }

        this.mq = window.matchMedia('(max-width: 768px)');

        const handleResize = () => {
            if (this.mq.matches) {
                this.panelTarget.classList.add('collapsed');

                if (this.hasToggleTarget) {
                    this.toggleTarget.setAttribute('aria-expanded', 'false');
                }
            } else {
                this.panelTarget.classList.remove('collapsed');

                if (this.hasToggleTarget) {
                    this.toggleTarget.setAttribute('aria-expanded', 'true');
                }
            }
        };

        handleResize();
        this.mq.addEventListener('change', handleResize);
    }
}
